/* PendingInvitesCard — surfaces incoming seller invites at the top of the
 * Buyer Hub dashboard. The most discoverable surface for a new buyer who
 * just got invited. Inline Accept / Decline buttons; auto-switches the
 * portal to the new supplier on Accept.
 *
 * Renders nothing when there are no pending invites. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, ArrowRight, X, Sparkles } from "lucide-react";
import { useSupplier } from "../context/SupplierContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

export default function PendingInvitesCard() {
  const { invites, acceptInvite, refresh } = useSupplier();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);   // invite token currently actioning

  if (!invites || invites.length === 0) return null;

  const onAccept = async (inv) => {
    setBusy(inv.token);
    try {
      const d = await acceptInvite(inv.token);
      toast.success(`Connected to ${d.tenant_name}`);
      nav("/buyer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept invite");
    } finally { setBusy(null); }
  };

  const onDecline = async (inv) => {
    if (!window.confirm(`Decline invite from ${inv.tenant_name}? They'll be notified.`)) return;
    setBusy(inv.token);
    try {
      await api.post(`/invites/${inv.token}/decline`);
      toast.success(`Declined invite from ${inv.tenant_name}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decline invite");
    } finally { setBusy(null); }
  };

  return (
    <div
      className="rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/10 to-emerald-50/40 p-5"
      data-testid="pending-invites-card"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center shrink-0">
            <Mail className="w-5 h-5"/>
          </div>
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
              You&apos;ve been invited
            </div>
            <div className="text-[15px] font-medium mt-0.5">
              {invites.length === 1
                ? `${invites[0].tenant_name} wants to sell to you`
                : `${invites.length} suppliers want to sell to you`}
            </div>
            <div className="text-[12px] text-foreground/60 mt-1 leading-snug">
              Accept to unlock their catalog, pricing and Net-terms instantly. No credit check needed.
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2" data-testid="pending-invites-list">
        {invites.map((inv) => {
          const b = inv.tenant_branding || {};
          const terms = (inv.default_terms || "NET_30").replace("NET_", "Net-");
          const credit = inv.additional_credit_limit || 0;
          return (
            <div
              key={inv.id}
              className="flex items-center gap-3 rounded-xl border border-foreground/10 bg-white px-3 py-2.5"
              data-testid={`pending-invite-${inv.id}`}
            >
              <span
                className="w-9 h-9 rounded-lg grid place-items-center text-[12px] font-bold display shrink-0"
                style={{ background: b.primary_color || "#0a1f1c", color: b.accent_color || "#e8ff3a" }}
              >
                {b.logo_url
                  ? <img src={b.logo_url} alt="" className="w-full h-full object-contain p-1"/>
                  : (inv.tenant_name || "?").slice(0, 1)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-medium truncate">{inv.tenant_name}</div>
                <div className="text-[11.5px] text-foreground/55 flex items-center gap-1.5 mt-0.5">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-foreground/[0.04] mono text-[10px] uppercase tracking-wider">
                    {terms}
                  </span>
                  {credit > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 mono text-[10px] uppercase tracking-wider">
                      <Sparkles className="w-2.5 h-2.5"/>+${credit.toLocaleString()} credit
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDecline(inv)}
                  disabled={busy === inv.token}
                  data-testid={`pending-invite-decline-${inv.id}`}
                  className="rounded-full text-[12px] h-8 px-3 text-foreground/65 hover:text-rose-700 hover:border-rose-200"
                >
                  <X className="w-3.5 h-3.5 mr-1"/>Decline
                </Button>
                <Button
                  size="sm"
                  onClick={() => onAccept(inv)}
                  disabled={busy === inv.token}
                  data-testid={`pending-invite-accept-${inv.id}`}
                  className="rounded-full text-[12px] h-8 px-3 bg-foreground text-background hover:bg-foreground/90"
                >
                  {busy === inv.token ? "Accepting…" : (<>Accept<ArrowRight className="w-3.5 h-3.5 ml-1"/></>)}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
